'use client';

import { deleteCookie, getCookie, setCookie } from 'cookies-next';

import { clientAxios } from '../api';
import {
  deleteAuthCookiesAtServer,
  getCookieAtServer,
} from '../utils/handleCookie';
import { logger, LogLevel } from '../utils';

import Auth from './Auth';

const A_WEEK = 60 * 60 * 24 * 7;

class ClientAuth {
  private static inflightPromise: Promise<string | null> | null = null;

  private static async _getWARTToken(): Promise<string | null> {
    const wSessionId = await getCookieAtServer('wsessionid');
    if (!wSessionId) return null;

    let WART = getCookie('WART') as string | null;

    if (!WART) {
      WART = await ClientAuth._setLegacyInterfaceWART(wSessionId);
    }

    return WART;
  }

  private static async _setLegacyInterfaceWART(wSessionId: string) {
    try {
      const { data } = await clientAxios.post('/legacyRefreshApi', {
        wSessionId,
      });

      if (data.ok) {
        await ClientAuth.authenticate(data.data.access, data.data.refresh);

        return data.data.access;
      } else {
        return null;
      }
    } catch (error) {
      logger(LogLevel.ERROR, error, {
        message: 'ClientAuth legacyRefreshApi error',
        detail: {
          error: error,
          stack: error instanceof Error ? error.stack : 'unknown',
        },
      });

      await ClientAuth.deAuthenticate();

      return null;
    }
  }

  static async getAccessToken(): Promise<string | null> {
    if (this.inflightPromise) return this.inflightPromise;

    this.inflightPromise = this._getAccessToken();

    this.inflightPromise.finally(() => {
      this.inflightPromise = null;
    });

    return this.inflightPromise;
  }

  private static async _getAccessToken(): Promise<string | null> {
    try {
      const WART = await this._getWARTToken();
      if (!WART) return null;

      const { accessTokenExpiredTime, refreshTokenExpiredTime } =
        Auth.getExpiredTime(WART);

      if (accessTokenExpiredTime === null || refreshTokenExpiredTime === null) {
        return null;
      }

      const now = Date.now();

      if (now < accessTokenExpiredTime) {
        const tokens = Auth.decodingToken(WART);
        return tokens?.accessToken || null;
      }

      if (now < refreshTokenExpiredTime) {
        const refreshToken = ClientAuth.getRefreshToken(WART);
        if (!refreshToken) return null;

        const newAccessToken = await Auth.refreshAccessToken(refreshToken);
        if (!newAccessToken) return null;

        await ClientAuth.authenticate(newAccessToken, refreshToken);
        return newAccessToken;
      }

      await ClientAuth.deAuthenticate();
      return null;
    } catch (error) {
      await ClientAuth.deAuthenticate();

      return null;
    }
  }

  static async authenticate(accessToken: string, refreshToken: string) {
    const encodedTokens = Auth.encodingToken({ accessToken, refreshToken });
    await ClientAuth.setTokenInCookie(encodedTokens);
  }

  static async deAuthenticate(): Promise<void> {
    await ClientAuth.deleteCookies();
  }

  static async deleteCookies(): Promise<void> {
    deleteCookie('WART', {
      domain: Auth.getDomain(),
      path: '/',
    });

    await deleteAuthCookiesAtServer();
  }

  static getRefreshToken(wart: string): string | null {
    const tokens = Auth.decodingToken(wart);

    if (!tokens) return null;

    return tokens.refreshToken;
  }

  static async setTokenInCookie(token: string) {
    const domain = Auth.getDomain();
    setCookie('WART', token, {
      path: '/',
      maxAge: A_WEEK,
      domain,
    });
  }
}

export default ClientAuth;
